import React, { useState, useEffect } from 'react';
import { strapiService } from '../../utils/api';
import { Card, KpiCard, SkeletonKpis, Tabs } from '../../components/app2';

// Paneles de cada pestaña
import CompanyPerformanceChart from './components/CompanyPerformanceChart';
import EmployeeEngagementMetrics from './components/EmployeeEngagementMetrics';
import SATConnectionMonitor from './components/SATConnectionMonitor';
import ExportReportsPanel from './components/ExportReportsPanel';

/**
 * Análisis del sistema (rediseño jul-2026). Es la pantalla de inicio del super
 * administrador: cuatro KPIs de toda la plataforma y un panel con pestañas.
 *
 * Aquí los KPIs SÍ traen su comparativo real (activeCompaniesChange,
 * totalEmployeesChange, totalReceiptsTrend vienen del backend), así que el
 * indicador que dibuja el diseño se llena con datos, no con adornos.
 * "Disponibilidad" sigue en N/A: no hay monitoreo de sistema y decirlo es más
 * honesto que inventar un 99.9 %.
 *
 * Se retiró el bloque "Acciones rápidas" (Sincronizar SAT · Backup ·
 * Configuración · Alertas): sus cuatro botones no tenían ninguna acción
 * asociada. Las rutas que sí existen viven en el menú lateral.
 *
 * Esta pantalla ABSORBE al antiguo /app/administrator-dashboard, que renderizaba
 * estos mismos cuatro paneles con KPIs escritos a mano y una gestión de empresas
 * inerte (lista vacía y formulario sin guardar). Su ruta ahora redirige aquí.
 */

const TABS = [
  { id: 'company-performance', label: 'Rendimiento empresas', icon: 'domain', component: CompanyPerformanceChart },
  { id: 'employee-engagement', label: 'Participación empleados', icon: 'group', component: EmployeeEngagementMetrics },
  { id: 'sat-connection', label: 'Conexión SAT', icon: 'verified_user', component: SATConnectionMonitor },
  { id: 'export-reports', label: 'Exportar reportes', icon: 'download', component: ExportReportsPanel },
];

/** Formatea el comparativo mensual que devuelve el backend. */
const deltaProps = (value, { suffix = '', unidad = 'este mes' } = {}) => {
  if (value === undefined || value === null) return {};
  const n = Number(value);
  return {
    note: `${n >= 0 ? '+' : ''}${n}${suffix} ${unidad}`,
    noteIcon: n >= 0 ? 'trending_up' : 'trending_down',
    noteTone: n >= 0 ? 'ok' : 'danger',
  };
};

const SystemAnalytics = () => {
  const [activeTab, setActiveTab] = useState('company-performance');
  const [dashboardStats, setDashboardStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchDashboardStats = async () => {
      try {
        const response = await strapiService.dashboard.getStats();
        setDashboardStats(response.data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardStats();
  }, []);

  const ActivePanel = TABS.find((t) => t.id === activeTab)?.component;

  return (
    <div className="a2-anim-screen">
      {loading ? (
        <SkeletonKpis count={4} />
      ) : (
        <div className="a2-grid-kpi" style={{ marginBottom: 16 }}>
          <KpiCard
            icon="domain"
            value={dashboardStats?.activeCompanies ?? 0}
            label="Empresas activas"
            {...deltaProps(dashboardStats?.activeCompaniesChange)}
          />
          <KpiCard
            icon="group"
            value={(dashboardStats?.totalEmployees ?? 0).toLocaleString('es-MX')}
            label="Empleados totales"
            {...deltaProps(dashboardStats?.totalEmployeesChange)}
          />
          <KpiCard
            icon="description"
            value={(dashboardStats?.totalReceipts ?? 0).toLocaleString('es-MX')}
            label="Nóminas procesadas"
            {...deltaProps(dashboardStats?.totalReceiptsTrend, { suffix: '%', unidad: 'vs mes anterior' })}
          />
          <KpiCard
            icon="monitor_heart"
            value="N/A"
            label="Disponibilidad"
            sub="Sin monitoreo de sistema"
            tone="warn"
          />
        </div>
      )}

      <Tabs items={TABS} value={activeTab} onChange={setActiveTab} style={{ marginBottom: 16 }} />

      <Card>{ActivePanel ? <ActivePanel /> : null}</Card>
    </div>
  );
};

export default SystemAnalytics;
